// Block C: the Aidey popup (design/business/mockups/chat.html, "Pop-up"), on the v2 tokens.
// 400 x 620 bottom right on a tablet or desktop, above where the Ask button sits; full screen on
// a phone (addendum 8), sized to the visual viewport so the composer stays above the keyboard.
// The class .aidey-popup-container is kept: FloatingAsk hides while it is on the page.
//
// It never opens itself. App renders it only when showAideyChat is true, and on a phone nothing
// sets that without a tap (decision 40, utils/chatAutoOpen.js).
import React, { useEffect, useRef } from 'react';
import { useBreakpoint } from '../../ui';
import { useKeyboardViewport } from '../../ui/Overlay';
import './chat.css';

export default function AideyPopup({ pageName, minimised = false, onToggleMinimise, onClose, onExpand, children }) {
  const ref = useRef(null);
  const phone = useBreakpoint() === 'phone';
  useKeyboardViewport(ref, phone);
  // Minimise is a desktop idea: a phone popup is full screen, so a remembered minimise is ignored.
  const min = minimised && !phone;
  // Escape closes the popup on a desktop (2026-10-08). Not when something inside already used the
  // key (the @mention list calls preventDefault), and not while a dialog is open over the page.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (phone) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      closeRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [phone]);
  return (
    <div ref={ref} className={`aidey-popup-container achat-pop${min ? ' achat-pop--min' : ''}`}
      role="region" aria-label="Aidey chat">
      <div className="achat-pop__head">
        <span className="achat-av achat-av--pop" aria-hidden="true">A</span>
        <div className="achat-pop__who">
          <span className="achat-pop__name">Aidey</span>
          {pageName && (
            <span className="achat-pop__ctx"><span className="achat-dot" aria-hidden="true" />Looking at: {pageName}</span>
          )}
        </div>
        <div className="achat-pop__actions">
          {onExpand && (
            <button type="button" className="achat-pop__btn achat-pop__btn--text" title="Open the full chat page" onClick={onExpand}>
              Expand <span aria-hidden="true">↗</span>
            </button>
          )}
          {!phone && (
            <button type="button" className="achat-pop__btn aidey-popup-minimise" onClick={onToggleMinimise}
              title={min ? 'Restore chat' : 'Minimise chat'} aria-label={min ? 'Restore chat' : 'Minimise chat'}>
              <span aria-hidden="true">{min ? '▴' : '–'}</span>
            </button>
          )}
          <button type="button" className="achat-pop__btn aidey-popup-close" onClick={onClose} title="Close" aria-label="Close chat">
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </div>
      {!min && children}
    </div>
  );
}
